import React, { useEffect, useState } from 'react';
import { updateStorage, getDefaultValue } from '../../services/storageManager'
import { getFiltersList } from '../../services/front_api';
import MultiChoiceSearchField from './MultiChoiceSearchField';

/**
 * ListFiltersCombo
 * Props:
 * - type: 'group' | 'entity' (détermine l'appel API par défaut)
 * - fetcher: optional function(parentIdArray, type) => Promise<array> (si fourni, utilisé à la place des helpers)
 * - parentIdArray: array optionnel passé à l'API
 * - apiType: optionnel, passé en second paramètre aux helpers (ex: 'someType')
 * - multiple: boolean, permet la sélection multiple
 * - value: valeur contrôlée (id ou array d'id) 
 * - onChange: function(newValue) appelé avec id ou array d'id
 * - labelField: nom du champ label dans les items (défaut 'label' ou 'name')
 * - valueField: nom du champ id dans les items (défaut 'id')
 * - placeholder: texte d'aide pour l'option vide
 */
const ListFiltersCombo = ({
    application, category, name, label, source,
    multiple = false,
    labelField = 'label',
    valueField = 'id',
    placeholder = 'Select...',
    size = 6,
    disabled = false,
    area = ''
}) => {
    // If multiple, delegate to MultiChoiceSearchField (same UI/behavior)
    if (multiple) {
        return (
            <MultiChoiceSearchField
                application={application}
                category={category}
                name={name}
                label={label}
                source='filter'
                area={source}
                labelField={labelField}
            />
        );
    }

    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(getDefaultValue(application, category, name) || []);
    const [error, setError] = useState(null);

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                let data = [];
                data = await getFiltersList(application, source);
                if (!mounted) return;
                // normalize to array of objects
                const normalized = Array.isArray(data) ? data : [];
                setOptions(normalized);
            } catch (err) {
                if (!mounted) return;
                setError(err);
                setOptions([]);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, [application, source, labelField]);

    const handleChange = (e) => {
        const selected = Array.from(e.target.selectedOptions).map(opt => {
            // option value contains the actual id stringified
            try { return JSON.parse(opt.value); } catch { return opt.value; }
        });
        // single select: take first or empty
        const valueToStore = selected.length > 0 ? selected[0] : '';
        updateStorage(application, category, name, valueToStore);
        setSelectedId(valueToStore);
    };

    // build option value as JSON string to preserve non-string ids
    const makeOptionValue = (item) => {
        const v = item[valueField] ?? item.id ?? item;
        return JSON.stringify(v);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {loading ? (
                <div style={{ fontSize: 13, color: '#666' }}>Chargement...</div>
            ) : error ? (
                <div style={{ color: 'red', fontSize: 13 }}>Erreur: {error.message || 'failed'}</div>
            ) : (
                <select
                    multiple={false}
                    value={selectedId}
                    onChange={handleChange}
                    size={undefined}
                    disabled={disabled}
                    style={{ padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', background: '#fff' }}
                >
                    <option value="">{placeholder}</option>
                    {options.map((opt, idx) => {
                        const labelText = opt["label"];
                        const val = makeOptionValue(opt);
                        return (
                            <option key={idx} value={val}>
                                {labelText}
                            </option>
                        );
                    })}
                </select>
            )}
        </div>
    );
};

export default ListFiltersCombo;